import { useState } from 'react';
import { PersonName } from '../../components/Avatar';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { lifeYears } from '../../domain/dates';
import { displayName } from '../../domain/names';
import { circlePortraitRight } from '../../domain/ownership';
import { kinLabel, kinshipKey } from '../../domain/relationships';
import type { PersonWithNames } from '../../domain/types';
import { useI18n } from '../../i18n';
import { useApp } from '../../app/AppState';
import { Link } from '../../app/router';
import { useRepos } from '../../repositories';
import { PortraitPicker } from '../people/PortraitPicker';
import { WhereLine } from '../people/WhereLine';
import { ReachButtons } from '../people/Reach';
import { RecognitionBar } from '../people/Recognition';
import { CircleOwnerPanel } from '../people/CircleOwner';
import { OccasionLine } from '../../components/Occasions';
import { AskQuestionDialog } from '../stories/AskQuestionDialog';

/**
 * Who somebody is, in words — **Details** on their constellation, and what a
 * face on a story, a moment or a profile opens.
 *
 * It is short on purpose. It used to carry their whole life underneath: two
 * tabs, three filters and a grid of everything they had posted or been tagged
 * in. That is the constellation's job now — their circle in the middle with
 * their pictures, portraits and stories around it — so what is left here is
 * the part a picture cannot tell you: their years, how they are related to
 * you, where they are, who looks after their circle, and the few things you
 * can do about them. Editing and locking still live on the full profile, so
 * the card stays a thing you read rather than a control panel.
 *
 * `standalone` makes it a real modal in the browser's top layer, which is what
 * lets it open *above* the constellation, a story or a profile panel and still
 * be usable — a plain overlay would sit behind them and go inert. A tap on the
 * backdrop or Escape puts it away.
 */
export function PersonCard({
  person,
  onClose,
  unconnected = false,
  standalone = false,
}: {
  person: PersonWithNames;
  onClose: () => void;
  /** This is the viewer's own circle and it has no relatives yet. */
  unconnected?: boolean;
  /** Opened over an ordinary screen rather than the tree: give it a backdrop that closes it. */
  standalone?: boolean;
}) {
  const { t, lang } = useI18n();
  const { me, graph, peopleById, actor, links, grants, stars, reload, openProfile } = useApp();
  const repos = useRepos();
  const isMe = me?.id === person.id;
  const starred = stars.has(person.id);
  const [asking, setAsking] = useState(false);
  // Ask them something: anyone else who is living (their circle need not be claimed — a helper may answer for them).
  const canAsk = !!actor && !isMe && person.status !== 'deceased';
  const toggleStar = () => {
    void repos.people.setStar(person.id, !starred).then((r) => {
      if (r.ok) reload();
    });
  };
  const kin = me && !isMe ? kinshipKey(graph, me.id, person.id, (id) => peopleById.get(id)?.gender ?? 'unspecified') : null;
  const years = lifeYears(person.birth, person.death, person.status === 'deceased', lang);
  // Why they may change the photo, not just whether: an administrator-only
  // right paints the camera in the admin colour. A circle nobody keeps accepts
  // its first face from anyone and nothing after that — see circlePortraitRight.
  const photoRight = actor ? circlePortraitRight(actor, links, grants, person) : 'none';
  const mayChangePhoto = photoRight !== 'none';

  const card = (
    <div className="person-card" role={standalone ? undefined : 'dialog'} aria-label={standalone ? undefined : displayName(person, lang)}>
        <div className="person-card-head">
          <PortraitPicker person={person} allowed={mayChangePhoto} adminOnly={photoRight === 'admin'} />
          <div className="who">
            <h2>
              <PersonName person={person} lang={lang} />
            </h2>
            {/* Years, how they are related to you, and where they are — one line that wraps, not three that stack. */}
            <div className="small muted card-meta">
              <span>
                {years}
                {years && (isMe || kin) ? ' · ' : ''}
                {isMe ? t('tree.card.you') : kin ? kinLabel(kin, lang) : ''}
              </span>
              <WhereLine person={person} nudge={isMe} />
            </div>
          </div>
          <div className="person-card-tools">
            {!isMe && (
              <button
                type="button"
                className={`btn btn-ghost btn-icon star-btn ${starred ? 'is-on' : ''}`}
                onClick={toggleStar}
                aria-pressed={starred}
                aria-label={starred ? t('tree.unstar') : t('tree.star')}
                title={starred ? t('tree.unstar') : t('tree.star')}
              >
                <Icon name="star" />
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-icon is-back" onClick={onClose} aria-label={t('nav.close')}>
              <Icon name="close" />
            </button>
          </div>
        </div>

        {/* Waiting to be recognised by the family — and the way to do it. */}
        <RecognitionBar person={person} className="mt-2" />
        {/* Who looks after this circle, so "why can't I change this photo?" is
            answered on the screen rather than by a permission error. */}
        <CircleOwnerPanel person={person} className="mt-2" />
        <OccasionLine personId={person.id} className="mt-2" />
        {unconnected && (
          <Link to={`/people/${person.id}/add`} className="card-nudge">
            <Icon name="link" />
            <span>{t('tree.meUnconnected')}</span>
          </Link>
        )}

        {/* Three things you can do from a circle: open the person, ask them
            something, message them. Everything else lives on their profile.
            The profile opens as a panel over this screen, so closing it brings
            you back to whatever you were reading rather than to a new place. */}
        <div className="person-card-icons" role="toolbar" aria-label={displayName(person, lang)}>
          <button
            type="button"
            className="fab-icon is-on"
            onClick={() => openProfile(person.id)}
            aria-label={isMe ? t('tree.card.myProfile') : t('tree.card.viewProfile')}
            title={isMe ? t('tree.card.myProfile') : t('tree.card.viewProfile')}
          >
            <Icon name="person" />
          </button>
          {canAsk && (
            <button type="button" className="fab-icon is-ask" onClick={() => setAsking(true)} aria-label={t('ask.button', { name: displayName(person, lang) })} title={t('ask.button', { name: displayName(person, lang) })}>
              <Icon name="question" />
            </button>
          )}
          <ReachButtons person={person} />
        </div>

        {/*
          * There is no **Pictures and stories** button here any more.
          *
          * This card *is* what the constellation's Details opens, so from there
          * the button led back to the screen you were already standing on; and
          * opened from a face inside a story it piled a third overlay on a
          * second, which is how Jay ended up on a screen whose Back button had
          * nowhere left to go. Their pictures and stories are the ring around
          * their circle — tapping them in the tree or on the globe is the one
          * way in, and it always has somewhere to come back to.
          */}

        {/* Inside .person-card: the sheet around it has pointer-events: none (taps beside the card reach the tree), and that is inherited. */}
        {canAsk && <AskQuestionDialog person={person} open={asking} onClose={() => setAsking(false)} />}
    </div>
  );

  // Over an ordinary screen the card is a modal, so it can stack over a panel.
  if (standalone) {
    return (
      <Dialog open onClose={onClose} title={displayName(person, lang)} labelledBy="person-card-title" variant="bare">
        {card}
      </Dialog>
    );
  }
  // In the tree it is drawn into the screen: taps beside it belong to the tree.
  return <div className="person-card-sheet">{card}</div>;
}
